import type { Rule } from '@/rule/domain/Rule.ts';
import { Optional } from '@/generic/functional';
import { RuleId } from '@/rule/domain/RuleId.ts';

type RulesBuilder = {
  rules: Map<string, Rule>;
};

export class Rules {
  readonly #rules: Map<string, Rule>;
  #listMemo: Optional<Rule[]> = Optional.empty();

  private constructor({ rules }: Partial<RulesBuilder> = {}) {
    this.#rules = Optional.ofFalsifiable(rules).orElseGet(() => new Map());
  }

  static create(): Rules {
    return new Rules();
  }

  add(...rules: Rule[]): Rules {
    const rulesMap = new Map(this.#rules);
    for (const rule of rules) {
      rulesMap.set(rule.id.value, rule);
    }
    return new Rules({ ...this.builder, rules: rulesMap });
  }

  getRule(id: RuleId | string) {
    return Optional.ofFalsifiable(this.#rules.get(RuleId.of(id).value));
  }

  filterByIds(ids: RuleId[]): Rules {
    const idValues = new Set(ids.map((id) => id.value));
    const rulesMap = new Map([...this.#rules].filter(([id]) => idValues.has(id)));
    return new Rules({ ...this.builder, rules: rulesMap });
  }

  get list(): Rule[] {
    const list = this.#listMemo.orElseGet(() => this.computeList());
    this.#listMemo = Optional.of(list);
    return list;
  }

  get total(): number {
    return this.#rules.size;
  }

  private computeList(): Rule[] {
    return [...this.#rules.values()];
  }

  private get builder(): RulesBuilder {
    return {
      rules: this.#rules,
    };
  }
}
